import { Button, ColorPicker } from 'antd';
import { DeviationType } from '../../../../types/types';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useContext, useEffect, useState } from 'react';
import { NotificationContext } from '../../../../providers/NotificationProvider';
import {
  createDeviation,
  CreateDeviationType,
  deleteDeviation,
  cloneDeviation,
  moveDeviationDown,
  moveDeviationUp,
  updateDeviation,
} from '../../../../features/projects/deviationSlice';
import Avatar from 'antd/es/avatar/avatar';
import { List } from 'antd/lib';
import { generateAvatar } from '../../../../features/utils/utils';

interface DeviationsProps {
  deviations: DeviationType[];
  nodeId: number;
  nodeOrder?: number;
  getDeviations: () => void;
  onSelectionChange?: (type: string, id: number) => void;
  updateTree: () => void; // Optional prop for updating the tree
}

const Deviations: React.FC<DeviationsProps> = ({
  deviations,
  nodeId,
  nodeOrder,
  getDeviations,
  onSelectionChange,
  updateTree,
}) => {
  const { triggerNotification } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const [editForm, setEditForm] = useState<boolean>(false);
  const [deviation, setDeviation] = useState<Partial<DeviationType>>({ name: '', color: '', nodeId: nodeId });
  const [movingId, setMovingId] = useState<number | null>(null);

  useEffect(() => {
    // Sync selected node with the deviation form to avoid stale nodeId.
    setEditForm(false);
    setDeviation({ name: '', color: '', nodeId });
  }, [nodeId]);

  const handleAdd = () => {
    if (!deviation.name || deviation.name === '' || !deviation.nodeId || typeof deviation.name === 'undefined') {
      triggerNotification('error', 'Error', 'El nombre de la desviación no puede estar vacío', 'bottomRight');
      return;
    }
    console.log('deviation', deviation);
    const trimmedColor = deviation.color?.trim();
    // if deviation.id exists, we are editing
    if (deviation.id) {
      const updatePayload: DeviationType = {
        ...(deviation as DeviationType),
        color: trimmedColor && trimmedColor.length > 0 ? trimmedColor : null,
      };
      dispatch(updateDeviation(updatePayload) as any)
        .then((result: any) => {
          if (updateDeviation.fulfilled.match(result)) {
            triggerNotification(
              'success',
              'Desviación actualizada',
              'La desviación ha sido actualizada correctamente',
              'bottomRight'
            );
            setEditForm(false);
            getDeviations();
            updateTree();
            // clear form
            setDeviation({ name: '', color: '', nodeId: nodeId });
          }
        })
        .catch((error: any) => {
          triggerNotification('error', 'Error', error.error, 'bottomRight');
        });

      return;
    }

    const deviationPayload: CreateDeviationType = {
      name: deviation.name ?? '',
      nodeId: deviation.nodeId ?? nodeId,
    };
    if (trimmedColor && trimmedColor.length > 0) {
      deviationPayload.color = trimmedColor;
    }

    dispatch(createDeviation(deviationPayload) as any)
      .then((result: any) => {
        if (createDeviation.fulfilled.match(result)) {
          triggerNotification(
            'success',
            'Desviación creada',
            'La desviación ha sido creada correctamente',
            'bottomRight'
          );
          setEditForm(false);
          getDeviations();
          updateTree();
          // clear form
          setDeviation({ name: '', color: '', nodeId: nodeId });
        }
      })
      .catch((error: any) => {
        triggerNotification('error', 'Error', error.error, 'bottomRight');
      });
  };

  return (
    <div>
      <h2>
        Desviaciones <Button type='primary' icon={<PlusOutlined />} onClick={() => setEditForm(true)}></Button>
      </h2>
      {editForm && (
        <div style={{ marginBottom: 20, padding: 20, backgroundColor: '#f9f9f9' }}>
          <h2>{deviation.id ? 'Editar desviación' : 'Nueva desviación'}</h2>
          <form>
            <div className='form-group'>
              <label style={{ marginRight: '10px' }}>Nombre:</label>
              <input
                type='text'
                placeholder='Nombre de la desviación'
                className='ant-input-deviation'
                value={deviation.name}
                onChange={(e) => setDeviation({ ...deviation, name: e.target.value })}
              />
            </div>
            <div className='form-group'>
              <label style={{ marginRight: '10px' }}>Color:</label>
              <ColorPicker
                value={deviation.color}
                onChange={(color) => setDeviation({ ...deviation, color: color.toHexString() })}
              />
            </div>
            <Button
              type='primary'
              onClick={() => {
                handleAdd();
              }}
            >
              Guardar
            </Button>
          </form>
        </div>
      )}
      {deviations.length > 0 ? (
        <List
          itemLayout='horizontal'
          dataSource={deviations}
          renderItem={(deviation, index) => {
            const id = deviation.id ?? 0;
            const isFirst = index === 0;
            const isLast = index === deviations.length - 1;
            const deviationNumber = index + 1;
            const sequenceLabel =
              nodeOrder !== undefined && nodeOrder !== null ? `${nodeOrder}.${deviationNumber}` : `${deviationNumber}`;

            const doMove = async (dir: 'up' | 'down') => {
              try {
                setMovingId(id);
                const thunk = dir === 'up' ? moveDeviationUp : moveDeviationDown;
                const result: any = await dispatch(thunk(id) as any);
                if (thunk.fulfilled.match(result)) {
                  await getDeviations();
                  await updateTree();
                } else {
                  // opcional: mostrar notificación de error
                  // triggerNotification('error', 'Error', result.payload?.error ?? 'No se pudo reordenar', 'bottomRight');
                }
              } finally {
                setMovingId(null);
              }
            };

            const doEdit = () => {
              setDeviation({
                name: deviation.name,
                color: deviation.color,
                nodeId: nodeId,
                id: deviation.id,
              });
              setEditForm(true);
            };

            const doDelete = () => {
              if (!deviation.id) return;
              dispatch(deleteDeviation(deviation) as any)
                .then((result: any) => {
                  if (deleteDeviation.fulfilled.match(result)) {
                    triggerNotification(
                      'success',
                      'Desviación eliminada',
                      'La desviación ha sido eliminada correctamente',
                      'bottomRight'
                    );
                    getDeviations();
                    updateTree();
                  } else {
                    triggerNotification(
                      'error',
                      'Error',
                      result.payload?.error ?? 'No se pudo eliminar',
                      'bottomRight'
                    );
                  }
                })
                .catch((error: any) => {
                  triggerNotification('error', 'Error', error?.error ?? 'No se pudo eliminar', 'bottomRight');
                });
            };
            const doClone = () => {
              if (!deviation.id) return;
              dispatch(cloneDeviation(deviation.id) as any)
                .then((result: any) => {
                  if (cloneDeviation.fulfilled.match(result)) {
                    triggerNotification(
                      'success',
                      'Desviación clonada',
                      'La desviación ha sido clonada correctamente',
                      'bottomRight'
                    );
                    getDeviations();
                    updateTree();
                  } else {
                    triggerNotification(
                      'error',
                      'Error',
                      result.payload?.error ?? 'No se pudo clonar',
                      'bottomRight'
                    );
                  }
                })
                .catch((error: any) => {
                  triggerNotification('error', 'Error', error?.error ?? 'No se pudo clonar', 'bottomRight');
                });
            };
            return (
              <List.Item
                actions={[
                  !isFirst && (
                    <Button
                      key='up'
                      type='text'
                      icon={<ArrowUpOutlined />}
                      disabled={movingId === id}
                      onClick={() => doMove('up')}
                    />
                  ),
                  !isLast && (
                    <Button
                      key='down'
                      type='text'
                      icon={<ArrowDownOutlined />}
                      disabled={movingId === id}
                      onClick={() => doMove('down')}
                    />
                  ),

                  // ✏️ Editar
                  <Button key='edit' type='link' icon={<EditOutlined />} onClick={doEdit} style={{ marginLeft: 10 }} />,
                  <Button key='clone' type='link' icon={<CopyOutlined />} onClick={doClone} style={{ marginLeft: 10 }} />,
                  <Button
                    key='delete'
                    type='link'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={doDelete}
                    style={{ marginLeft: 10 }}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar src={generateAvatar(sequenceLabel, deviation.color ?? '#ffffff')} />}
                  title={
                    <>
                      <button
                        type='button'
                        onClick={() => {
                          if (onSelectionChange) {
                            onSelectionChange('deviation', deviation.id ?? 0);
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#666',
                          textDecoration: 'none',
                          font: 'inherit',
                        }}
                      >
                        {`${deviation.name}`}
                      </button>
                    </>
                  }
                />
              </List.Item>
            );
          }}
        />
      ) : (
        <p>No hay desviaciones creadas para este nodo</p>
      )}
    </div>
  );
};

export default Deviations;
